/**
 * @file billPoEdits.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-22
 * @description Changes to a bill's POs typed in Add Bill (Cam, 5 Oct 2026),
 *              held until the bill is saved and sent with it as `po_edits`
 *              (server: utils/addBillPoEdits, billService._applyAddBillPoEdits).
 *
 * Held as { [poId]: { [poLineKey]: { description?, quantity?, unitAmount? } } },
 * text as typed. A field typed back to the PO's own value is no change.
 * A PO-H and a Direct Delivery haulage line are not editable (the server
 * refuses them too): both are worked out from the customer order.
 *
 * 1.1.0 (2026-10-06, Cam): a PO made in Xero, or whose customer order is
 * Delivered, takes a COST change only — its description and quantity are not
 * sent (`scope` from POST /api/purchase-orders/billing-context; the server
 * refuses them too). And the user's choice to record the bill's quantity as
 * SUPPLIED on a PO line with none is sent as `supplied_copies`.
 *
 * 1.2.0 (2026-10-09, Cam): a delivered (or invoiced) order's PO takes a
 * DESCRIPTION correction as well as its cost — words are our own record; the
 * quantity stays as delivered. A PO made in Xero is still cost only.
 *
 * 1.3.0 (2026-10-22, Cam): an edit may put the PO line in its bill's units —
 * `units: { take }` (utils/poUnitsFromBill, worked out in Add Bill from the
 * bill's own figures). Sent as `units: 'bill'` (the server works the maths
 * out again from the bill); shown as the line will be — quantity and cost in
 * the bill's unit, its unit weight with them, the amount unchanged.
 */

import { poLineKey } from './billLineReconcile';
import { getTaxRate } from './taxTypes';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const round4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;
const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const words = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Can this PO line be changed from Add Bill? */
export const poLineEditable = (po, li) => !!po && po.po_type !== 'haulage' && !!li && li.kind !== 'haulage';

/** Cost only: a PO made in Xero, or whose order is delivered (its billing-context scope). */
export const costOnly = (scope) => !!scope;

/** May a line's description be corrected? Not on a PO made in Xero (its words live in Xero). */
export const descriptionEditable = (scope) => !scope || scope.mode === 'delivered';

/** The fields of `edit` that differ from the PO line, as the server takes them. */
function changedFields(li, edit, scope = null) {
  const out = {};
  if (!edit) return out;
  // The PO line in its bill's units: on its own (the server refuses it with
  // a quantity or cost beside it), never on a PO made in Xero.
  if (edit.units?.take?.ok && scope?.mode !== 'xero_made') {
    out.units = 'bill';
    if (descriptionEditable(scope) && edit.description !== undefined && words(edit.description) && words(edit.description) !== words(li.description)) {
      out.description = words(edit.description);
    }
    return out;
  }
  const onlyCost = costOnly(scope);
  if (!onlyCost && edit.quantity !== undefined && num(edit.quantity) !== null && round4(num(edit.quantity)) !== round4(li.quantity)) {
    out.quantity = round4(num(edit.quantity));
  }
  if (edit.unitAmount !== undefined && num(edit.unitAmount) !== null && round4(num(edit.unitAmount)) !== round4(li.unitAmount)) {
    out.unit_amount = round4(num(edit.unitAmount));
  }
  if (descriptionEditable(scope) && edit.description !== undefined && words(edit.description) && words(edit.description) !== words(li.description)) {
    out.description = words(edit.description);
  }
  return out;
}

/**
 * A PO line as it will be once the edits are saved — for display.
 * @returns {{ quantity, unitAmount, description, lineAmount, taxAmount, unitWeightKg, relabelled: boolean, changed: Set<string> }}
 */
export function effectivePoLine(li, edit, scope = null) {
  const changed = changedFields(li || {}, edit, scope);
  if (changed.units) {
    const take = edit.units.take;
    return {
      quantity: take.quantity,
      unitAmount: take.unitAmount,
      description: changed.description ?? (li?.description || ''),
      // Kept its cost (it was in the bill's unit already): the amount follows.
      lineAmount: take.costKept ? take.amount : (num(li?.lineAmount) ?? take.amount),
      taxAmount: take.costKept
        ? round2(take.amount * (num(li?.lineAmount) ? (num(li?.taxAmount) || 0) / num(li.lineAmount) : 0))
        : (num(li?.taxAmount) ?? 0),
      unitWeightKg: take.toKg,
      relabelled: true,
      changed: new Set(Object.keys(changed)),
    };
  }
  const quantity = changed.quantity ?? (num(li?.quantity) ?? 0);
  const unitAmount = changed.unit_amount ?? (num(li?.unitAmount) ?? 0);
  const moved = changed.quantity !== undefined || changed.unit_amount !== undefined;
  const oldNet = num(li?.lineAmount);
  const rate = oldNet && Math.abs(oldNet) >= 0.01 && num(li?.taxAmount) !== null
    ? num(li.taxAmount) / oldNet
    : (getTaxRate(li?.taxType) || 0);
  const lineAmount = moved ? round2(quantity * unitAmount) : (oldNet ?? round2(quantity * unitAmount));
  const taxAmount = moved ? round2(lineAmount * rate) : (num(li?.taxAmount) ?? round2(lineAmount * rate));
  return {
    quantity,
    unitAmount,
    description: changed.description ?? (li?.description || ''),
    lineAmount,
    taxAmount,
    unitWeightKg: li?.unitWeightKg ?? null,
    relabelled: false,
    changed: new Set(Object.keys(changed)),
  };
}

/**
 * Everything to send with the bill: one entry per changed PO line.
 * @param {object} edits — { [poId]: { [key]: edit } }
 * @param {object} poById — { [poId]: { po_type, line_items } }
 * @param {object} [scopes] — { [poId]: scope|null } (cost only where set)
 * @returns {Array<{ purchase_order_id: number, po_line_key: string, quantity?, unit_amount?, description? }>}
 */
export function poEditsPayload(edits, poById, scopes = {}) {
  const out = [];
  for (const [poId, byKey] of Object.entries(edits || {})) {
    const po = poById?.[poId];
    const lines = Array.isArray(po?.line_items) ? po.line_items : [];
    for (const [key, edit] of Object.entries(byKey || {})) {
      const index = lines.findIndex((l, i) => poLineKey(l, i) === key);
      if (index < 0 || !poLineEditable(po, lines[index])) continue;
      const changed = changedFields(lines[index], edit, scopes?.[poId] || null);
      if (Object.keys(changed).length) out.push({ purchase_order_id: Number(poId), po_line_key: key, ...changed });
    }
  }
  return out;
}

/** The PO quantity a bill line is compared with: the edited one when changed. */
export function poQuantityFor(li, edit, scope = null) {
  return effectivePoLine(li, edit, scope).quantity;
}

/**
 * The PO lines whose supplied quantity the user chose to copy from the bill,
 * as the server takes them — only ones still offered (no supplied quantity,
 * nothing refusing it, billed by a line of this bill).
 * @param {object} copies — { [poId]: { [key]: true | 'confirmed' } } ('confirmed':
 *        far from what was ordered, and the user said yes — sent as confirmed: true)
 * @param {(poId: string|number, key: string) => boolean} offered
 * @returns {Array<{ purchase_order_id: number, po_line_key: string }>}
 */
export function suppliedCopiesPayload(copies, offered) {
  const out = [];
  for (const [poId, byKey] of Object.entries(copies || {})) {
    for (const [key, on] of Object.entries(byKey || {})) {
      if (on && (!offered || offered(poId, key))) {
        out.push({ purchase_order_id: Number(poId), po_line_key: key, ...(on === 'confirmed' ? { confirmed: true } : {}) });
      }
    }
  }
  return out;
}
